import { useEffect, useState } from 'react';
import { serverNow } from '../../time-sync';
import { ClockDisplay, Digit, Well } from './digits';
import { PlayerPanel, type ScoreboardPlayer } from './PlayerPanel';
import { THEMES, type DigitStyle, type ScoreboardConfig, type ThemeKey } from './themes';

export interface ScoreboardGameState {
  home: { name: string; score: number; logo: string };
  away: { name: string; score: number; logo: string };
  clock: { m: number; s: number };
  period: number;
  shotClock: number;
  poss: 'home' | 'away' | null;
  bonus: { home: boolean; away: boolean };
  fouls: { home: number; away: number };
  tol: { home: number; away: number };
  players: { home: ScoreboardPlayer[]; away: ScoreboardPlayer[] };
}

const DEFAULT_GAME: ScoreboardGameState = {
  home: { name: 'WILDCATS', score: 102, logo: '🐾' },
  away: { name: 'HAWKS', score: 98, logo: '🦅' },
  clock: { m: 2, s: 6 },
  period: 4,
  shotClock: 14,
  poss: 'home',
  bonus: { home: true, away: false },
  fouls: { home: 8, away: 3 },
  tol: { home: 3, away: 5 },
  players: {
    home: [
      { no: 2, name: 'JOHNSON', f: 3, pts: 28 },
      { no: 15, name: 'WILLIAMS', f: 4, pts: 22 },
      { no: 25, name: 'CARTER', f: 1, pts: 18 },
      { no: 11, name: 'ROBINSON', f: 2, pts: 21 },
      { no: 22, name: 'DAVIS', f: 4, pts: 13 },
    ],
    away: [
      { no: 12, name: 'THOMAS', f: 1, pts: 26 },
      { no: 3, name: 'HARRIS', f: 0, pts: 19 },
      { no: 35, name: 'BROWN', f: 3, pts: 15 },
      { no: 21, name: 'MARTINEZ', f: 2, pts: 24 },
      { no: 8, name: 'JACKSON', f: 1, pts: 14 },
    ],
  },
};

function useClock(init: { m: number; s: number }) {
  const [t, setT] = useState(init);
  useEffect(() => {
    const i = setInterval(
      () =>
        setT((p) => {
          const total = p.m * 60 + p.s - 1;
          return total < 0 ? p : { m: Math.floor(total / 60), s: total % 60 };
        }),
      1000,
    );
    return () => clearInterval(i);
  }, []);
  return t;
}

interface LiveState {
  home: { score: number; fouls: number; timeoutsRemaining: number; bonus: boolean; doubleBonus?: boolean };
  away: { score: number; fouls: number; timeoutsRemaining: number; bonus: boolean; doubleBonus?: boolean };
  period: number;
  clock: {
    m: number;
    s: number;
    running: boolean;
    startedAt?: string | null;
  };
  possession: 'home' | 'away' | null;
  playerStats?: Record<string, Record<string, number>>;
  onCourt?: { home: string[]; away: string[] };
  playerStints?: Record<string, string>;
  intermission?: 'halftime' | 'period_break' | null;
  intermissionEndsAt?: string | null;
  sportState?: Record<string, unknown>;
}

interface LiveRosterPlayer {
  id: string;
  name: string;
  jerseyNumber: string | null;
  position: string | null;
}

interface LiveRosterSide {
  teamId: string;
  name: string;
  abbreviation: string | null;
  colorPrimary: string | null;
  players: LiveRosterPlayer[];
}

interface LiveSnapshot {
  gameId: string;
  state: LiveState;
  rosters: { home: LiveRosterSide; away: LiveRosterSide };
}

/**
 * Map server roster + live player stats into the local
 * ScoreboardPlayer shape used by the existing PlayerPanel. Players with
 * 4+ fouls render their foul digit in amber automatically inside the
 * panel (the component already keys off that threshold).
 */
function formatMinSec(seconds: number): string {
  if (seconds <= 0) return '';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds - m * 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function rosterToPanelPlayers(
  side: LiveRosterSide,
  stats: Record<string, Record<string, number>> | undefined,
  onCourtIds: string[] | undefined,
  stints: Record<string, string> | undefined,
  nowMs: number,
): ScoreboardPlayer[] {
  // When a lineup is set, the on-screen panel shows only players currently
  // on the court (in the order the operator picked them). When the lineup
  // is empty (operator hasn't set one yet), fall back to the first N by
  // jersey order so the panel is never blank.
  const inGame =
    onCourtIds && onCourtIds.length > 0
      ? (onCourtIds
          .map((id) => side.players.find((p) => p.id === id))
          .filter(Boolean) as LiveRosterPlayer[])
      : side.players.slice(0, 5);
  return inGame.map((p) => {
    const s = stats?.[p.id];
    const closed = typeof s?.sec === 'number' ? s.sec : 0;
    const openSince = stints?.[p.id];
    const live = openSince
      ? closed + Math.max(0, Math.floor((nowMs - new Date(openSince).getTime()) / 1000))
      : closed;
    return {
      no: p.jerseyNumber ?? '-',
      name: p.name.toUpperCase(),
      f: typeof s?.f === 'number' ? s.f : 0,
      pts: typeof s?.pts === 'number' ? s.pts : 0,
      min: formatMinSec(live),
    };
  });
}

function useLiveCountdown(
  m: number,
  s: number,
  running: boolean,
  startedAt: string | null | undefined,
): { m: number; s: number } {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!running) return;
    const i = setInterval(() => setTick((x) => x + 1), 250);
    return () => clearInterval(i);
  }, [running]);
  void tick;

  if (!running || !startedAt) return { m, s };
  const elapsedSec = Math.max(
    0,
    Math.floor((serverNow() - new Date(startedAt).getTime()) / 1000),
  );
  const total = Math.max(0, m * 60 + s - elapsedSec);
  return { m: Math.floor(total / 60), s: total % 60 };
}

function useLiveShotClock(
  value: number,
  running: boolean,
  startedAt: string | null | undefined,
): number {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!running) return;
    const i = setInterval(() => setTick((x) => x + 1), 250);
    return () => clearInterval(i);
  }, [running]);
  void tick;

  if (!running || !startedAt) return value;
  const elapsedSec = Math.max(
    0,
    Math.floor((serverNow() - new Date(startedAt).getTime()) / 1000),
  );
  return Math.max(0, value - elapsedSec);
}

export function Scoreboard({
  config,
  snapshot,
  game = DEFAULT_GAME,
  isSample = true,
  connected,
}: {
  config: ScoreboardConfig | null | undefined;
  snapshot?: LiveSnapshot | null;
  game?: ScoreboardGameState;
  isSample?: boolean;
  connected: boolean;
}) {
  const live = snapshot?.state;
  const themeKey: ThemeKey = (config?.theme as ThemeKey) ?? 'classic';
  const mode: DigitStyle = (config?.digitStyle as DigitStyle) ?? 'segment';
  const P = THEMES[themeKey] ?? THEMES.classic;
  const sponsorTop = config?.sponsorTop;
  const sponsorBottom = config?.sponsorBottom;
  const homeLogo = config?.homeLogo ?? game.home.logo;
  const awayLogo = config?.awayLogo ?? game.away.logo;

  // Live game state overrides sample. Rosters come from snapshot.rosters
  // when available; fall back to sample only when nothing is linked.
  const homeScore = live?.home.score ?? game.home.score;
  const awayScore = live?.away.score ?? game.away.score;
  const homeFouls = live?.home.fouls ?? game.fouls.home;
  const awayFouls = live?.away.fouls ?? game.fouls.away;
  const homeTOL = live?.home.timeoutsRemaining ?? game.tol.home;
  const awayTOL = live?.away.timeoutsRemaining ?? game.tol.away;
  const homeBonus = live?.home.bonus ?? game.bonus.home;
  const awayBonus = live?.away.bonus ?? game.bonus.away;
  const homeDoubleBonus = live?.home.doubleBonus ?? false;
  const awayDoubleBonus = live?.away.doubleBonus ?? false;
  const possession = live?.possession ?? game.poss;
  const period = live?.period ?? game.period;

  // Roster panel sources. When live, build from snapshot.rosters + live
  // playerStats; when sample, use the DEFAULT_GAME static players.
  // 1-Hz tick so live minutes-played update while a stint is open.
  const [minTick, setMinTick] = useState(0);
  useEffect(() => {
    if (!live?.playerStints || Object.keys(live.playerStints).length === 0) {
      return;
    }
    const id = setInterval(() => setMinTick((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, [live?.playerStints]);
  void minTick;
  const nowMs = serverNow();

  // Responsive digit sizing — scores and clock auto-fit the viewport so
  // they're always as big as possible without horizontal overflow.
  // Math: each digit is 0.58*h wide; score takes ~1.88*h + 96 (Well pad);
  // clock takes ~2.14*h + 96. With grid gap 56 + outer pad 48,
  //   total = 2 * (1.88*h_score + 96) + (2.14*h_clock + 96) + gap + pad
  // Setting h_score = 1.25 * h_clock gives:
  //   h_clock = (vw - 48 - 56 - 192 - 96) / (2*1.88*1.25 + 2.14) = (vw - 392) / 6.84
  const [vw, setVw] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1920,
  );
  useEffect(() => {
    const handler = () => setVw(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  const hClock = Math.max(
    200,
    Math.min(420, Math.floor((vw - 392) / 6.84)),
  );
  const hScore = Math.max(240, Math.min(520, Math.floor(hClock * 1.25)));

  const homePanelPlayers = snapshot
    ? rosterToPanelPlayers(
        snapshot.rosters.home,
        live?.playerStats,
        live?.onCourt?.home,
        live?.playerStints,
        nowMs,
      )
    : game.players.home;
  const awayPanelPlayers = snapshot
    ? rosterToPanelPlayers(
        snapshot.rosters.away,
        live?.playerStats,
        live?.onCourt?.away,
        live?.playerStints,
        nowMs,
      )
    : game.players.away;

  // Team names also come from snapshot when live, allowing the panel
  // header to read "TIGERS" / "KNIGHTS" instead of the sample team names.
  const homeTitle = snapshot?.rosters.home.name.toUpperCase() ?? game.home.name;
  const awayTitle = snapshot?.rosters.away.name.toUpperCase() ?? game.away.name;

  // Sample mode runs a local count-down for visual effect; live mode
  // ticks down using the server's authoritative startedAt + clock value.
  const sampleClock = useClock(game.clock);
  const liveClock = useLiveCountdown(
    live?.clock.m ?? 0,
    live?.clock.s ?? 0,
    !!live?.clock.running,
    live?.clock.startedAt,
  );
  const clockSrc = live ? liveClock : sampleClock;

  // Shot clock: in live mode, read from sportState; otherwise local tick.
  const liveSS = (live?.sportState ?? {}) as Record<string, unknown>;
  const liveShotClockValue =
    typeof liveSS.shotClock === 'number' ? liveSS.shotClock : 24;
  const liveShotClockRunning =
    typeof liveSS.shotClockRunning === 'boolean' ? liveSS.shotClockRunning : false;
  const liveShotClockStartedAt =
    typeof liveSS.shotClockStartedAt === 'string' ? liveSS.shotClockStartedAt : null;
  const liveSc = useLiveShotClock(
    liveShotClockValue,
    liveShotClockRunning,
    liveShotClockStartedAt,
  );

  const [sampleSc, setSampleSc] = useState(game.shotClock);
  useEffect(() => {
    if (live) return;
    const i = setInterval(() => setSampleSc((p) => (p <= 0 ? 24 : p - 1)), 1000);
    return () => clearInterval(i);
  }, [live]);

  const sc = live ? liveSc : sampleSc;

  return (
    <div
      style={{
        width: '100%',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: P.bg,
        fontFamily: "'Outfit', 'Segoe UI', sans-serif",
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {mode === 'bulb' && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            opacity: 0.01,
            backgroundImage:
              'radial-gradient(circle, #fff 0.4px, transparent 0.4px)',
            backgroundSize: '3px 3px',
            zIndex: 0,
          }}
        />
      )}

      {/* Top sponsor */}
      {sponsorTop?.name && (
        <div
          style={{
            padding: '8px 16px',
            textAlign: 'center',
            background: P.ac,
            borderBottom: `3px solid ${P.br}`,
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Well pad="8px 28px">
            <div>
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 800,
                  color: '#e8c84a',
                  letterSpacing: 4,
                  textShadow: '0 0 8px rgba(232,200,74,0.3)',
                }}
              >
                {sponsorTop.name}
              </div>
              {sponsorTop.tagline && (
                <div
                  style={{
                    fontSize: 8,
                    color: '#666',
                    letterSpacing: 2,
                    marginTop: 2,
                  }}
                >
                  {sponsorTop.tagline}
                </div>
              )}
            </div>
          </Well>
        </div>
      )}

      {/* Main scoreboard — top: huge score/clock band, bottom: rosters */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          padding: '20px 24px',
          gap: 20,
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* ─── TEAM NAMES band ─────────────────────────────── */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            alignItems: 'center',
            gap: 24,
          }}
        >
          <TeamNameBlock
            label="HOME"
            name={homeTitle}
            color={snapshot?.rosters.home.colorPrimary ?? '#dc2626'}
          />
          <div style={{ width: 1 }} />
          <TeamNameBlock
            label="VISITOR"
            name={awayTitle}
            color={snapshot?.rosters.away.colorPrimary ?? '#1d4ed8'}
            alignRight
          />
        </div>

        {/* ─── MAIN BIG ROW: SCORE | CLOCK | SCORE ───────────── */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            alignItems: 'center',
            gap: 28,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Well pad="32px 48px">
              <Digit
                val={homeScore}
                digits={homeScore >= 100 ? 3 : 2}
                color="#ff1a1a"
                h={hScore}
                mode={mode}
              />
            </Well>
          </div>

          {/* Clock + period stack */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 14,
            }}
          >
            <div
              style={{
                fontSize: 18,
                fontWeight: 900,
                color: P.sub,
                letterSpacing: 6,
              }}
            >
              GAME CLOCK
            </div>
            <Well pad="28px 48px">
              <ClockDisplay
                m={clockSrc.m}
                s={clockSrc.s}
                color="#22c55e"
                h={hClock}
                mode={mode}
              />
            </Well>

            {/* PERIOD / HALFTIME below the clock */}
            <div style={{ marginTop: 4 }}>
              {live?.intermission === 'halftime' ? (
                <HalftimeBadge endsAt={live.intermissionEndsAt} />
              ) : (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                  }}
                >
                  <span
                    style={{
                      fontSize: 18,
                      fontWeight: 900,
                      color: P.sub,
                      letterSpacing: 5,
                    }}
                  >
                    PERIOD
                  </span>
                  <Well pad="6px 16px">
                    <Digit
                      val={period}
                      digits={1}
                      color="#ff1a1a"
                      h={64}
                      mode={mode}
                    />
                  </Well>
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Well pad="32px 48px">
              <Digit
                val={awayScore}
                digits={awayScore >= 100 ? 3 : 2}
                color="#ff1a1a"
                h={hScore}
                mode={mode}
              />
            </Well>
          </div>
        </div>

        {/* ─── STATS row: home stats | shot clock | away stats ─── */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            alignItems: 'stretch',
            gap: 24,
          }}
        >
          <SideStatsBlock
            possession={possession === 'home'}
            possSide="home"
            bonus={homeBonus}
            doubleBonus={homeDoubleBonus}
            fouls={homeFouls}
            tol={homeTOL}
            mode={mode}
            theme={P}
            alignRight
          />

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
              padding: '12px 22px',
              background: P.ac,
              borderRadius: 10,
              border: `3px solid ${P.br}`,
            }}
          >
            <div
              style={{
                fontSize: 16,
                fontWeight: 900,
                color: '#fbbf24',
                letterSpacing: 5,
              }}
            >
              SHOT CLOCK
            </div>
            <Well pad="6px 14px">
              <Digit
                val={sc}
                digits={2}
                color={sc <= 5 ? '#ff1a1a' : '#fbbf24'}
                h={90}
                mode={mode}
              />
            </Well>
          </div>

          <SideStatsBlock
            possession={possession === 'away'}
            possSide="away"
            bonus={awayBonus}
            doubleBonus={awayDoubleBonus}
            fouls={awayFouls}
            tol={awayTOL}
            mode={mode}
            theme={P}
          />
        </div>

        {/* ─── ROSTER row (two side-by-side panels) ─────────── */}
        <div
          style={{
            flex: 1,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 16,
            minHeight: 0,
          }}
        >
          <PlayerPanel
            players={homePanelPlayers}
            title={homeTitle}
            logo={homeLogo}
            theme={P}
            mode={mode}
          />
          <PlayerPanel
            players={awayPanelPlayers}
            title={awayTitle}
            logo={awayLogo}
            theme={P}
            mode={mode}
          />
        </div>
      </div>

      {/* Bottom sponsor */}
      {sponsorBottom?.name && (
        <div
          style={{
            padding: '8px 16px',
            textAlign: 'center',
            background: P.ac,
            borderTop: `3px solid ${P.br}`,
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Well pad="7px 28px">
            <div>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 800,
                  color: '#cccccc',
                  letterSpacing: 3,
                }}
              >
                {sponsorBottom.name}
              </div>
              {sponsorBottom.tagline && (
                <div
                  style={{
                    fontSize: 8,
                    color: '#555',
                    letterSpacing: 2,
                    marginTop: 1,
                  }}
                >
                  {sponsorBottom.tagline}
                </div>
              )}
            </div>
          </Well>
        </div>
      )}

      {/* Sample-data + connection indicator (tiny corner badge) */}
      <div
        style={{
          position: 'absolute',
          right: 8,
          bottom: sponsorBottom?.name ? 50 : 8,
          fontSize: 9,
          color: 'rgba(255,255,255,0.35)',
          letterSpacing: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          zIndex: 2,
        }}
      >
        <span
          style={{
            display: 'inline-block',
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: connected ? '#22c55e' : '#fbbf24',
            boxShadow: connected ? '0 0 6px #22c55e88' : '0 0 6px #fbbf2488',
          }}
        />
        {isSample ? 'SAMPLE DATA' : 'LIVE'}
      </div>
    </div>
  );
}

function HalftimeBadge({ endsAt }: { endsAt: string | null | undefined }) {
  const [, force] = useState(0);
  useEffect(() => {
    if (!endsAt) return;
    const id = setInterval(() => force((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, [endsAt]);

  let label = '';
  let expired = false;
  if (endsAt) {
    const remaining = Math.max(
      0,
      Math.floor((new Date(endsAt).getTime() - serverNow()) / 1000),
    );
    const m = Math.floor(remaining / 60);
    const s = remaining - m * 60;
    label = `${m}:${String(s).padStart(2, '0')}`;
    expired = remaining === 0;
  }

  const color = expired ? '#ef4444' : '#fbbf24';
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        padding: '14px 32px',
        border: `3px solid ${color}`,
        borderRadius: 12,
        background: expired
          ? 'rgba(239,68,68,0.12)'
          : 'rgba(251,191,36,0.12)',
        textShadow: expired
          ? '0 0 14px rgba(239,68,68,0.4)'
          : '0 0 14px rgba(251,191,36,0.4)',
      }}
    >
      <div
        style={{
          fontSize: 28,
          fontWeight: 900,
          color,
          letterSpacing: 8,
        }}
      >
        HALFTIME
      </div>
      <div
        style={{
          fontFamily: 'ui-monospace, monospace',
          fontSize: 64,
          fontWeight: 900,
          color,
          letterSpacing: 4,
          lineHeight: 1,
        }}
      >
        {label || '—'}
      </div>
    </div>
  );
}

function TeamNameBlock({
  label,
  name,
  color,
  alignRight,
}: {
  label: string;
  name: string;
  color: string;
  alignRight?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: alignRight ? 'flex-end' : 'flex-start',
        gap: 4,
      }}
    >
      <div
        style={{
          fontSize: 18,
          fontWeight: 900,
          letterSpacing: 6,
          color: '#9aa',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 52,
          fontWeight: 900,
          color: '#fff',
          letterSpacing: 1,
          lineHeight: 1,
          textShadow: '0 2px 6px rgba(0,0,0,0.6)',
        }}
      >
        {name}
      </div>
      <div
        style={{
          width: 160,
          height: 6,
          background: color,
          borderRadius: 3,
          boxShadow: `0 0 14px ${color}88`,
        }}
      />
    </div>
  );
}

function SideStatsBlock({
  possession,
  possSide,
  bonus,
  doubleBonus,
  fouls,
  tol,
  mode,
  theme,
  alignRight,
}: {
  possession: boolean;
  possSide: 'home' | 'away';
  bonus: boolean;
  doubleBonus?: boolean;
  fouls: number;
  tol: number;
  mode: DigitStyle;
  theme: { ac: string; br: string; sub: string };
  alignRight?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: alignRight ? 'flex-end' : 'flex-start',
        gap: 20,
        background: theme.ac,
        borderRadius: 10,
        border: `3px solid ${theme.br}`,
        padding: '14px 22px',
      }}
    >
      {alignRight && (
        <PossIndicator side={possSide} active={possession} />
      )}
      <StatTile label="FOULS" color="#ff1a1a">
        <Well pad="4px 10px">
          <Digit val={fouls} digits={1} color="#ff1a1a" h={64} mode={mode} />
        </Well>
      </StatTile>
      <StatTile label="TOL" color="#22c55e">
        <Well pad="4px 10px">
          <Digit val={tol} digits={1} color="#22c55e" h={64} mode={mode} />
        </Well>
      </StatTile>
      <BonusPill active={bonus} double={doubleBonus} />
      {!alignRight && (
        <PossIndicator side={possSide} active={possession} />
      )}
    </div>
  );
}

function StatTile({
  label,
  color,
  children,
}: {
  label: string;
  color: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div
        style={{
          fontSize: 13,
          fontWeight: 900,
          color,
          letterSpacing: 3,
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function BonusPill({ active, double }: { active: boolean; double?: boolean }) {
  const on = active || double;
  // Double bonus shows red; single bonus amber; off is dim.
  const color = double ? '#ff4d4d' : on ? '#fbbf24' : '#3a3a30';
  return (
    <div
      style={{
        fontSize: double ? 14 : 16,
        fontWeight: 900,
        letterSpacing: double ? 2 : 4,
        padding: '8px 14px',
        borderRadius: 6,
        textAlign: 'center',
        color,
        background: on
          ? double
            ? 'rgba(255,77,77,0.15)'
            : 'rgba(251,191,36,0.15)'
          : 'transparent',
        border: `2px solid ${on ? color : 'transparent'}`,
        textShadow: on ? `0 0 10px ${color}66` : 'none',
      }}
    >
      {double ? 'DBL BONUS' : 'BONUS'}
    </div>
  );
}

function PossIndicator({
  side,
  active,
}: {
  side: 'home' | 'away';
  active: boolean;
}) {
  const arrow = side === 'home' ? '◀' : '▶';
  return (
    <div
      style={{
        fontSize: 30,
        fontWeight: 900,
        letterSpacing: 2,
        color: active ? '#ff1a1a' : '#2a1010',
        textShadow: active ? '0 0 12px #ff1a1aaa' : 'none',
        padding: '0 6px',
      }}
      title="Possession"
    >
      {side === 'home' && arrow}
      <span
        style={{
          fontSize: 14,
          color: active ? '#fff' : '#3a2a2a',
          letterSpacing: 4,
          padding: '0 6px',
        }}
      >
        POSS
      </span>
      {side === 'away' && arrow}
    </div>
  );
}
